import {
  ContactsOutlined,
  FormatListBulleted,
  HelpOutline,
  KeyOutlined,
  Logout,
  PeopleOutline,
  SettingsOutlined,
  WorkspacesOutlined,
} from '@mui/icons-material'
import {
  Link,
  LinkProps,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import { FC, useRef, useState } from 'react'
import { NavLink, Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom'

import useUser from '../../../hooks/useUser'
import Divider from '../Divider'
import InfoList from '../InfoList'
import Menu from '../Menu'
import SignoutModal from '../Modals/LogoutModal'
import NameAvatar from '../NameAvatar'

interface NavbarAvatarProps {}

const NavbarAvatar: FC<NavbarAvatarProps> = () => {
  const avatarRef = useRef<HTMLDivElement>()
  const { loading: loadingUser, user } = useUser()
  const [open, setOpen] = useState(false)
  const [openLogoutModal, setOpenLogoutModal] = useState(false)

  const { firstName, fullName, isAdmin, lastName, username } = user ?? {}

  if (loadingUser) {
    return <Skeleton height={36} width={36} variant="circular" />
  }

  if (!user) {
    return null
  }

  return (
    <>
      <NameAvatar
        firstName={firstName}
        lastName={lastName}
        opacity={1}
        ref={avatarRef}
        sx={{ cursor: 'pointer' }}
        onClick={() => setOpen((open) => !open)}
      />

      <Menu
        anchorEl={avatarRef.current}
        open={open}
        paperProps={{ sx: { minWidth: 250, overflow: 'hidden' } }}
        placement="bottom-end"
        onClose={() => setOpen(false)}
      >
        <Stack spacing={2} sx={{ pb: 6 / 8 }}>
          <Stack
            alignItems="center"
            direction="row"
            spacing={2}
            sx={{
              bgcolor: 'grey.50',
              borderBottom: '2px solid',
              borderBottomColor: 'grey.100',
              p: 2,
            }}
          >
            <NameAvatar size="large" firstName={firstName} lastName={lastName} />

            <Stack>
              <Typography color="grey.700" fontWeight={500}>
                {fullName}
              </Typography>

              <Typography color="grey.500" variant="caption">
                {username}
              </Typography>

              {isAdmin && (
                <Typography color="grey.500" sx={{ mt: 0.5 }} variant="caption">
                  Administrator
                </Typography>
              )}
            </Stack>
          </Stack>

          <List dense disablePadding onClick={() => setOpen(false)}>
            {/* {isAdmin && (
              <>
                <ListItem component={NavLink} to="/app/settings/access-management">
                  <ListItemIcon>
                    <KeyOutlined />
                  </ListItemIcon>

                  <ListItemText primary="Permissions" />
                </ListItem>

                <ListItem component={NavLink} to="/app/settings/teams">
                  <ListItemIcon>
                    <WorkspacesOutlined />
                  </ListItemIcon>

                  <ListItemText primary="Teams" />
                </ListItem>

                <ListItem component={NavLink} to="/app/settings/users">
                  <ListItemIcon>
                    <PeopleOutline />
                  </ListItemIcon>

                  <ListItemText primary="Users" />
                </ListItem>

                <Divider sx={{ my: 0.5 }} />
              </>
            )} */}

            <ListItem component={NavLink} to="/app/manage/contacts">
              <ListItemIcon>
                <ContactsOutlined />
              </ListItemIcon>

              <ListItemText primary="Contacts" />
            </ListItem>

            <ListItem component={NavLink} to="/app/manage/terms">
              <ListItemIcon>
                <FormatListBulleted />
              </ListItemIcon>

              <ListItemText primary="Contract Configuration" />
            </ListItem>

            <ListItem component={NavLink} to="/app/settings">
              <ListItemIcon>
                <SettingsOutlined />
              </ListItemIcon>

              <ListItemText primary="Settings" />
            </ListItem>

            <Divider sx={{ my: 0.5 }} />

            <HoverableListItem onClick={() => window.zE.activate({ hideOnClose: true })}>
              <ListItemIcon>
                <HelpOutline />
              </ListItemIcon>

              <ListItemText primary="Help" />
            </HoverableListItem>

            <HoverableListItem onClick={() => setOpenLogoutModal(true)}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>

              <ListItemText primary="Sign Out" />
            </HoverableListItem>
          </List>

          <Stack
            direction="row"
            spacing={2}
            sx={{ px: 2, mt: (theme) => `${theme.spacing(1)} !important` }}
          >
            <InfoList columnGap={1}>
              <StyledLink
                to="/app/support/privacy"
                variant="caption"
                onClick={() => setOpen(false)}
              >
                Privacy
              </StyledLink>

              <StyledLink to="/app/support/terms" variant="caption" onClick={() => setOpen(false)}>
                Terms & Conditions
              </StyledLink>
            </InfoList>
          </Stack>
        </Stack>
      </Menu>

      <SignoutModal open={openLogoutModal} onClose={() => setOpenLogoutModal(false)} />
    </>
  )
}

const StyledLink = styled((props: LinkProps & RouterLinkProps) => (
  <Link component={RouterLink} {...props} />
))`
  color: ${({ theme }) => theme.palette.grey[500]};
`

const HoverableListItem = styled(ListItem)`
  cursor: pointer;
`

export default NavbarAvatar
